import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { DatatableMultiSelectFilterComponent } from '../../../shared/tables/datatable/datatable-filters/datatable-multi-select-filter/datatable-multi-select-filter.component';
import { DatatableBadge, DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import {
  AssociatedDevice,
  IgnoredEntry,
  JobState,
  JobStatus,
  LineUsageType,
  LineUsageTypeV2,
  MigrationStatusV2,
  NumberReportEntry,
  NumberStatusV2,
  RingCentralFields,
  TeamsNumberSource,
  ToTeamsNumberV2,
  UserStatusV2,
} from '../../../shared/models/generated/ziroModels';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, of, Subscription, take } from 'rxjs';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';
import { MigrationsNumberContext, MigrationsNumberReport } from '../../services/migrations-numbers-context.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { TranslateService } from '@ngx-translate/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../button/button.component';
import { ZiroIcons, ZiroStackedIcons } from '../../../shared/models/ziro-icons.enum';
import { delayWhen, filter, finalize, map, switchMap } from 'rxjs/operators';
import { ZpmUserPhotoContext } from '../../../shared/contexts/zpm-user-photo.context';
import moment from 'moment';
import { ToastService } from '../../../shared/services/toast.service';
import {
  migrationsNumberSortFn,
  MigrationsService,
  UiLineType,
  UiMigrationStatus,
} from '../../services/migrations.service';
import { RcWidgetFilter } from '../migrations-ring-central-dashboard/migrations-ring-central-dashboard.component';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { DatatableFieldTypes, DatatableTextField } from '../../../shared/tables/datatable/datatable-field.models';
import { ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { Microsoft365UserResource } from '../../../helpdesk/shared/resources/microsoft-365-user.resource';
import { DatatableFilterService } from '../../../shared/tables/datatable/datatable-filter.service';

export interface RingCentralNumberReportRow extends DatatableRow {
  id?: string;
  migrationStatus?: string;
  isMigrationStatusPending?: boolean;
  phoneNumber?: string;
  extension?: string;
  lineUsageType?: string;
  deviceUsageType?: string;
  userEmail?: string;
  userName?: string;
  site?: string;
  associatedDevices?: string[];
  teamsUser?: string;
  teamsNumber?: string;
}

@Component({
  selector: 'ziro-migrations-ring-central-numbers-report',
  templateUrl: './migrations-ring-central-numbers-report.component.html',
  styleUrls: ['../../migrations.component.scss'],
  standalone: false,
})
export class MigrationsRingCentralNumbersReportComponent implements OnInit, OnDestroy {
  @ViewChild('datatable') datatableComponent: DatatableComponent<RingCentralNumberReportRow>;
  @ViewChild('migrationStatusFilter') migrationStatusFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('lineTypeFilter') lineTypeFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('deviceTypeFilter') deviceTypeFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('deviceUsageTypeCell', { static: true }) deviceUsageTypeCellTemplate: TemplateRef<any>;

  readonly MAX_ASSOCIATED_DEVICE_LENGTH = 30;
  readonly MAX_ASSOCIATED_DEVICE_BADGE_LENGTH = this.MAX_ASSOCIATED_DEVICE_LENGTH - 10;
  protected readonly ZiroStackedIcons = ZiroStackedIcons;

  private static DEFAULT_HIDDEN_COLUMNS: string[] = [
    'tkey;migrations.ring_central.numbers_report.table.site.label',
    'tkey;migrations.ring_central.numbers_report.table.devices.label',
  ];

  private readonly _lineTypeOptionsInOrder: string[] = [
    UiLineType.USER,
    UiLineType.CONTACT_CENTER_AGENT,
    UiLineType.SHARED_DEVICE,
    UiLineType.SHARED_LINE,
    UiLineType.MULTI_LINE_USER,
    UiLineType.VOICE_APP,
    UiLineType.ANALOG,
    UiLineType.INACTIVE,
    UiLineType.AUTO_ATTENDANT,
    UiLineType.ORPHAN,
    UiLineType.UNCLASSIFIED,
  ];
  private readonly _refreshButtonId = 'migrations-number-report-refresh-button';
  private readonly _ignoreUnignoreRowsButtonId = 'migrations-number-report-ignore-unignore-rows-button';

  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _migrationsService = inject(MigrationsService);
  private _route = inject(ActivatedRoute);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _ringCentralNumberReportContext = inject(MigrationsNumberContext);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _zpmUserPhotoContext = inject(ZpmUserPhotoContext);
  private _toastService = inject(ToastService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _microsoft365UserResource = inject(Microsoft365UserResource);
  private _datatableFilterService = inject(DatatableFilterService);

  private _tenantId: number;
  private _assessmentName: string;
  private _filter: [RcWidgetFilter, string];
  private _numberReport: MigrationsNumberReport<RingCentralFields>;
  private _subscriptions = new Subscription();
  private _ringCentralNumberReportSubscription: Subscription;

  manualEntryIcon = this._getIconHtml(ZiroIcons.MANUAL_ENTRY);
  translationsIcon = this._getIconHtml(ZiroIcons.NUMBER_TRANSLATIONS);
  buttonStyles = ButtonStyles;
  tableDataAutomation = 'ring-central-number-report-table';
  cdrDiffDays: number;
  utilizationSummary: string;
  isLoading = true;
  showCustomAlert = false;
  isManualRefresh = false;
  isRefreshing = false;
  data: RingCentralNumberReportRow[] = [];
  migrationStatusOptionsWithCounts: FilterSelectOption[] = [];
  lineTypeOptionsWithCounts: FilterSelectOption[] = [];
  deviceTypeOptionsWithCounts: FilterSelectOption[] = [];
  initialGroupedColumnFilterOptions: FilterSelectOption[];
  groupedColumnFilterOptions: FilterSelectOption[] = [
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.migration_status.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.migration_status.label',
      groupBy: '&nbsp',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.phone_number.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.phone_number.label',
      groupBy: '&nbsp',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.extension.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.extension.label',
      groupBy: '&nbsp',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.usage_type.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.usage_type.label',
      groupBy: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.device_type.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.device_type.label',
      groupBy: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.user_email.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.user_email.label',
      groupBy: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.user_name.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.user_name.label',
      groupBy: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.site.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.site.label',
      groupBy: 'tkey;migrations.numbers_report.table.sub_label.associated_device_information.label',
    },
    {
      value: 'tkey;migrations.ring_central.numbers_report.table.devices.label',
      label: 'tkey;migrations.ring_central.numbers_report.table.devices.label',
      groupBy: 'tkey;migrations.numbers_report.table.sub_label.associated_device_information.label',
    },
  ];

  migrationStatusMatchFn = (filter: string[], row: RingCentralNumberReportRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.migrationStatus);

  lineTypeMatchFn = (filter: string[], row: RingCentralNumberReportRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.lineUsageType);

  deviceTypeMatchFn = (filter: string[], row: RingCentralNumberReportRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.deviceUsageType);

  columns: DatatableColumn<RingCentralNumberReportRow>[] = [
    {
      name: 'migrationStatus',
      label: 'tkey;migrations.ring_central.numbers_report.table.migration_status.label',
      subLabel: '&nbsp;',
      columnCharacterWidth: 12,
      pipe: {
        transform: (value: string, isPending: boolean): string =>
          this._migrationsService.getMigrationStatusHtml(value, isPending),
      },
      pipeArgs: ['isMigrationStatusPending'],
    },
    {
      name: 'phoneNumber',
      label: 'tkey;migrations.ring_central.numbers_report.table.phone_number.label',
      subLabel: '&nbsp;',
      pipe: {
        transform: (value: string): string => {
          return `<strong>${this._telephoneNumberFilter.transform(value)}</strong>`;
        },
      },
      columnCharacterWidth: 20,
      sortFn: (a: string, b: string): number => migrationsNumberSortFn(a, b),
    },
    {
      name: 'extension',
      label: 'tkey;migrations.ring_central.numbers_report.table.extension.label',
      subLabel: '&nbsp;',
      pipe: {
        transform: (value: string): string => {
          return this._telephoneNumberFilter.transform(value);
        },
      },
    },
    {
      name: 'lineUsageType',
      label: 'tkey;migrations.ring_central.numbers_report.table.usage_type.label',
      subLabel: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 20,
    },
    {
      name: 'deviceUsageType',
      label: 'tkey;migrations.ring_central.numbers_report.table.device_type.label',
      subLabel: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 20,
      cellTemplate: () => this.deviceUsageTypeCellTemplate,
    },
    {
      name: 'userEmail',
      label: 'tkey;migrations.ring_central.numbers_report.table.user_email.label',
      subLabel: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
    },
    {
      name: 'userName',
      label: 'tkey;migrations.ring_central.numbers_report.table.user_name.label',
      subLabel: 'tkey;migrations.numbers_report.table.sub_label.ai_insights.label',
    },
    {
      name: 'site',
      label: 'tkey;migrations.ring_central.numbers_report.table.site.label',
      subLabel: '&nbsp;',
    },
    {
      name: 'associatedDevices',
      label: 'tkey;migrations.ring_central.numbers_report.table.devices.label',
      subLabel: '&nbsp;',
      columnCharacterWidth: this.MAX_ASSOCIATED_DEVICE_LENGTH,
      sortFn: (a: string[], b: string[]) => (a[0] ?? '').localeCompare(b[0] ?? ''),
    },
    {
      name: 'teamsUser',
      label: 'tkey;migrations.ring_central.numbers_report.table.teams_user.label',
      subLabel: 'tkey;migrations.numbers_report.table.sub_label.move_to_microsoft_teams.label',
      colClass: 'move-to-teams-user-col',
      columnCharacterWidth: 30,
    },
    {
      name: 'teamsNumber',
      label: 'tkey;migrations.ring_central.numbers_report.table.teams_number.label',
      subLabel: 'tkey;migrations.numbers_report.table.sub_label.move_to_microsoft_teams.label',
      colClass: 'move-to-teams-number-col',
      columnCharacterWidth: 25,
    },
  ];

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.params.tenantId;
    this._filter = this._route.snapshot.paramMap.get('filter')?.split('=') as [RcWidgetFilter, string];
    this._initContexts();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  protected onRowSelected(): void {
    this._initBottomNav();
  }

  onFilterInitialized(): void {
    if (!this._filter) {
      return;
    }
    setTimeout(() => {
      const type: RcWidgetFilter = this._filter[0];
      const filter: string = decodeURIComponent(this._filter[1]);

      if (!this.lineTypeFilter?.selectedItems.length && type === 'lineType') {
        this.lineTypeFilter?.onFilterChange(filter);
        this._changeDetectorRef.detectChanges();
      } else if (!this.deviceTypeFilter?.selectedItems.length && type === 'deviceType') {
        this.deviceTypeFilter?.onFilterChange(filter);
        this._changeDetectorRef.detectChanges();
      } else if (!this.migrationStatusFilter?.selectedItems.length && type === 'migrationStatus') {
        this.migrationStatusFilter?.onFilterChange(filter);
        this._changeDetectorRef.detectChanges();
      }
    });
  }

  protected onRefreshClick(): void {
    this.isRefreshing = true;
    this.isManualRefresh = true;
    this._migrationsService.dispatchButtonState(this._refreshButtonId, {
      pending: true,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );

    this._ringCentralNumberReportContext.refresh().subscribe();
  }

  private _initContexts(): void {
    const assessmentId = this._route.snapshot.paramMap.get('id');
    const sub = combineLatest([
      this._migrationsAssessmentsContext.state$,
      this._ringCentralNumberReportContext.jobState$,
    ]).subscribe(([allExports, reportJobStatus]) => {
      this._assessmentName = allExports.ringCentralExports.find((rc) => rc.exportId === assessmentId)?.name ?? '';
      this._initBreadcrumbs();
      this._initBottomNav();
      this._handleContextPolling(reportJobStatus);

      if (reportJobStatus.jobState === JobState.IDLE && reportJobStatus.lastRunTime !== null) {
        if (reportJobStatus.error !== null) {
          this.showCustomAlert = true;
          this.isLoading = false;
        } else {
          this.showCustomAlert = false;
          this._getReportData();
        }
        if (this.isManualRefresh) {
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.REFRESH,
            'tkey;migrations.number_report.refresh_toast.title',
            'tkey;migrations.number_report.refresh_toast.message',
          );
          this.isManualRefresh = false;
        }
        this.isRefreshing = false;
      }
    });
    this._subscriptions.add(sub);
  }

  private _initBreadcrumbs(): void {
    const assessmentId = this._route.snapshot.paramMap.get('id');
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: this._assessmentName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/ring-central/${assessmentId}`,
      },
      { label: 'tkey;migrations.ring_central.numbers_report.title' },
    ]);
  }

  private _initBottomNav(): void {
    const selectedRows = MigrationsService.getSelectedRows(this.data);
    const selectedRowCount = selectedRows.length;
    const allSelectedRowsAreIgnored =
      selectedRowCount > 0 ? MigrationsService.areAllSelectedRowsIgnored(selectedRows) : false;

    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: this._refreshButtonId,
          dataAutomation: 'migrations-number-report-refresh-button',
          label: 'tkey;migrations.number_report.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.DEFAULT,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: (): void => this.onRefreshClick(),
        },
        {
          id: this._ignoreUnignoreRowsButtonId,
          dataAutomation: 'migrations-number-report-ignore-unignore-rows-button',
          label: !selectedRowCount
            ? 'tkey;migrations.number_report.ignore_unignore_row.button.disabled.label'
            : allSelectedRowsAreIgnored
              ? this._translateService.instant('tkey;migrations.number_report.unignore_rows.button.label', {
                  rowNumbers: selectedRowCount,
                })
              : this._translateService.instant('tkey;migrations.number_report.ignore_rows.button.label', {
                  rowNumbers: selectedRowCount,
                }),
          buttonClass: ButtonStyles.SECONDARY,
          icon: !selectedRowCount ? undefined : allSelectedRowsAreIgnored ? ZiroIcons.UNIGNORE : ZiroIcons.IGNORE,
          state: {
            pending: false,
            tooltipVisible: !selectedRowCount,
            buttonDisableState: {
              disabled: !selectedRowCount,
              tooltipKey: 'tkey;migrations.number_report.ignore_unignore_row.button.disabled.tooltip',
            },
          },
          cb: (): void => this._ignoreSelectedRows(selectedRows, allSelectedRowsAreIgnored),
        },
      ]),
    );
  }

  private _ignoreSelectedRows(selectedRows: RingCentralNumberReportRow[], allSelectedRowsAreIgnored: boolean): void {
    this._migrationsService.dispatchButtonState(this._refreshButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._migrationsService.dispatchButtonState(this._ignoreUnignoreRowsButtonId, {
      pending: true,
      buttonDisableState: {
        disabled: true,
      },
    });

    const newIgnoredList: IgnoredEntry[] = MigrationsService.buildIgnoredEntriesList(
      this.data,
      selectedRows,
      allSelectedRowsAreIgnored,
    );

    selectedRows.forEach((row) => {
      row.isMigrationStatusPending = true;
    });

    this._ringCentralNumberReportContext
      .updateIgnoredDirectoryNumbers(newIgnoredList)
      .pipe(
        take(1),
        finalize(() => this._resetButtonStates()),
      )
      .subscribe({
        next: () => {
          this.data = MigrationsService.deselectAllRows(this.data);
        },
      });
  }

  private _resetButtonStates(): void {
    const selectedRowCount = MigrationsService.getSelectedRows(this.data).length;

    this._migrationsService.dispatchButtonState(this._refreshButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: false,
      },
    });
    this._migrationsService.dispatchButtonState(this._ignoreUnignoreRowsButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: !selectedRowCount,
        tooltipKey: 'tkey;migrations.number_report.bottom_nav.ignore_unignore_row.disabled',
      },
    });
  }

  private _handleContextPolling(reportJobStatus: JobStatus): void {
    const isJobStatusIdle = reportJobStatus.jobState === JobState.IDLE;

    if (isJobStatusIdle) {
      this._migrationsService.setBottomNavTimestamp(
        reportJobStatus.lastRunTime,
        'tkey;migrations.number_report.refresh_button.last_updated_label',
      );
      this._migrationsService.dispatchButtonState(this._refreshButtonId, {
        pending: false,
        buttonDisableState: {
          disabled: false,
        },
      });
    } else {
      this.isRefreshing = true;
      this._migrationsService.dispatchButtonState(this._refreshButtonId, {
        pending: true,
        buttonDisableState: {
          disabled: true,
        },
      });
      this._migrationsService.dispatchButtonState(this._ignoreUnignoreRowsButtonId, {
        pending: false,
        buttonDisableState: {
          disabled: true,
        },
      });
    }
  }

  private _getReportData(): void {
    this._ringCentralNumberReportSubscription?.unsubscribe();
    this._ringCentralNumberReportSubscription = this._ringCentralNumberReportContext.state$.subscribe(
      (numberReport: MigrationsNumberReport<RingCentralFields>) => {
        this._numberReport = numberReport;
        const upns: string[] = Array.from(
          new Set(numberReport.entries.map((entry) => entry.toTeams?.toTeamsUser?.upn)),
        );
        this._zpmUserPhotoContext.initUserPhotos(this._tenantId.toString(), upns).subscribe();

        this._setUtilizationSummary();
        this.initialGroupedColumnFilterOptions = [...this.groupedColumnFilterOptions].filter(
          (option: FilterSelectOption) =>
            !MigrationsRingCentralNumbersReportComponent.DEFAULT_HIDDEN_COLUMNS.includes(option.label),
        );
        this._buildRows(numberReport.entries);
        this.isLoading = false;
      },
    );

    this._subscriptions.add(this._ringCentralNumberReportSubscription);
  }

  private _setUtilizationSummary(): void {
    if (!this._numberReport) {
      return;
    }

    const oldestMoment = moment(new Date(this._numberReport.oldestCdrTimestamp));
    const newestMoment = moment(new Date(this._numberReport.mostRecentCdrTimestamp));
    this.cdrDiffDays = newestMoment.diff(oldestMoment, 'days');
    const formattedOldestDate = moment(oldestMoment).format('MMMM Do, YYYY');
    const formattedNewestDate = moment(newestMoment).format('MMMM Do, YYYY');

    if (!this._numberReport.totalNumberOfCdrs) {
      this.utilizationSummary = this._translateService.instant(
        'tkey;migrations.number_report.table.utilization.empty.temp',
      );
    } else if (this._numberReport.totalNumberOfCdrs && this.cdrDiffDays < 30) {
      this.utilizationSummary = this._translateService.instant(
        'tkey;migrations.number_report.table.utilization.short',
        {
          totalNumberOfCdrs: this._numberReport.totalNumberOfCdrs,
          timespan: this.cdrDiffDays,
          startDate: formattedOldestDate,
          endDate: formattedNewestDate,
        },
      );
    } else {
      this.utilizationSummary = this._translateService.instant(
        'tkey;migrations.number_report.table.utilization.in_use',
        {
          totalNumberOfCdrs: this._numberReport.totalNumberOfCdrs,
          timespan: this.cdrDiffDays,
          startDate: formattedOldestDate,
          endDate: formattedNewestDate,
        },
      );
    }
  }

  private _buildRows(entries: NumberReportEntry<RingCentralFields>[]): void {
    const rows: RingCentralNumberReportRow[] = entries.map((entry: NumberReportEntry<RingCentralFields>) => {
      const teamsNumberObject = this._getMoveToTeamsNumberObject(
        entry.toTeams.toTeamsNumber,
        entry?.number,
        entry?.rowId,
        entry?.migrationStatus,
      );

      return {
        id: entry.rowId,
        migrationStatus: MigrationsService.MIGRATION_STATUS_ROW_MAP.get(entry.migrationStatus).label,
        phoneNumber: entry.number,
        extension: entry.pbxFields.extension,
        lineUsageType: MigrationsService.LINE_TYPE_ROW_MAP.get(entry.numberInsights.lineUsageType).lineType,
        deviceUsageType: MigrationsService.DEVICE_TYPE_ROW_MAP.get(entry.numberInsights.deviceUsageType).label,
        userEmail: entry.pbxFields.associatedUser?.email,
        userName:
          `${entry.pbxFields.associatedUser?.firstName ?? ''} ${entry.pbxFields.associatedUser?.lastName ?? ''}`.trim(),
        site: entry.pbxFields.site,
        associatedDevices: entry.pbxFields.associatedDevices?.map((device) => device.name) ?? [],
        teamsUser: entry.toTeams.toTeamsUser.upn,
        teamsNumber: entry.toTeams.toTeamsNumber.number,
        isTableRowSelectable: true,
        html: {
          lineUsageType: this._buildLineDisplayMarkup(entry.numberInsights.lineUsageType),
        },
        badges: {
          associatedDevices: {
            maxLength: this.MAX_ASSOCIATED_DEVICE_BADGE_LENGTH,
            values: this._buildAssociatedDeviceBadges(
              entry.pbxFields.associatedDevices,
              'tkey;migrations.number_report.table.associated_devices.tooltip.header',
            ),
          },
        },
        fields: {
          teamsUser: {
            type: DatatableFieldTypes.SELECT,
            value: {
              value: entry.toTeams.toTeamsUser.upn,
              label: entry.toTeams.toTeamsUser.upn,
            },
            showHtml: true,
            asyncOptionsFn: (query) => {
              if (!query || query.length < 3) {
                return of([]);
              }
              return this._microsoft365UserResource.search(this._route.snapshot.params.tenantId, query).pipe(
                delayWhen((users) => {
                  if (!users.length) {
                    return of([]);
                  }
                  // Only include the first 15 results. The photo search endpoint is pretty unoptimized.
                  const upns = users.map((user) => user.microsoft365UserRef.id).slice(0, 15);
                  return this._zpmUserPhotoContext.initUserPhotos(this._route.snapshot.params.tenantId, upns);
                }),
                map((users) => {
                  const photos = this._zpmUserPhotoContext.getUserPhotos();
                  return users.map((result) => {
                    const photo = photos.find(
                      (p) => p.userPrincipalName === result.microsoft365UserRef.id,
                    )?.photoBase64;
                    const image = photo
                      ? `<img src="data:image/png;base64,${photo}" class="display-pic-size user-profile-picture" alt="display picture" />`
                      : `<i class="display-pic-size no-user-icon ${ZiroIcons.CIRCLE_USER}"></i>`;
                    return {
                      value: result.microsoft365UserRef.id,
                      label: result.microsoft365UserRef.id,
                      optionHtml: `${image} <span class="ms-4">${result.microsoft365UserRef.id} | ${result.microsoft365UserRef.displayName}</span>`,
                    };
                  });
                }),
              );
            },
            base64ImageSearchFn: (value) => {
              if (!value) {
                return of(null);
              }
              return new Observable((subscriber) => {
                const loadingIcon = `<i class="position-absolute text-warning ${ZiroIcons.LOADING}"></i>`;
                subscriber.next(loadingIcon);
                this._zpmUserPhotoContext
                  .initUserPhotos(this._route.snapshot.params.tenantId, [value])
                  .pipe(
                    switchMap(() => {
                      if (entry.toTeams.toTeamsUser?.upn !== value) {
                        return this._ringCentralNumberReportContext.state$.pipe(
                          map(
                            (report) => report.entries.find((val) => val.number === entry.number)?.toTeams.toTeamsUser,
                          ),
                          filter((toTeamsUser) => toTeamsUser?.upn === value),
                          take(1),
                        );
                      }
                      return of(entry.toTeams.toTeamsUser);
                    }),
                    map(() => {
                      const photo = this._zpmUserPhotoContext
                        .getUserPhotos()
                        .find((photo) => photo.userPrincipalName === value)?.photoBase64;
                      return photo
                        ? `<img src="data:image/png;base64,${photo}" class="display-pic-size user-profile-picture" alt="display picture"/>`
                        : `
                    <span class="display-pic-size">
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 2667 2667"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        xml:space="preserve"
                        xmlns:serif="http://www.serif.com/"
                        style="
                          fill-rule: evenodd;
                          clip-rule: evenodd;
                          stroke-linecap: round;
                          stroke-linejoin: round;
                          stroke-miterlimit: 1.5;
                        "
                      >
                        <path
                          d="M1929.167,1867.5c-92.083,-160 -265,-267.5 -462.5,-267.5l-266.667,0c-197.5,0 -370.417,107.5 -462.5,267.5c146.667,163.333 359.167,265.833 595.833,265.833c236.667,0 449.167,-102.917 595.833,-265.833Zm-1662.5,-534.167c-0,-589.167 477.5,-1066.667 1066.667,-1066.667c589.167,-0 1066.667,477.5 1066.667,1066.667c0,589.167 -477.5,1066.667 -1066.667,1066.667c-589.167,0 -1066.667,-477.5 -1066.667,-1066.667Zm1066.667,66.667c165.833,0 300,-134.167 300,-300c0,-165.833 -134.167,-300 -300,-300c-165.833,0 -300,134.167 -300,300c0,165.833 134.167,300 300,300Z"
                          style="fill-rule: nonzero"
                        />
                        <circle cx="1333.333" cy="1333.333" r="1100" style="fill:none;stroke-width:187.5px;" />
                      </svg>
                    </span>
                    `;
                    }),
                  )
                  .subscribe((html) => {
                    subscriber.next(html);
                    subscriber.complete();
                  });
              });
            },
            imageBorderClass: () => this._getUpnPhotoBorderColor(entry.toTeams.toTeamsUser.userStatus),
            onSelect: (opt: FilterSelectOption) => this._onMs365UserInputSelect(entry, opt.value),
            tooltip:
              entry.migrationStatus === MigrationStatusV2.IGNORED
                ? this._translateService.instant('tkey;migrations.number_report.ignored.row.disabled.tooltip')
                : entry.toTeams.toTeamsUser.userStatus !== UserStatusV2.NO_STATUS &&
                    entry.toTeams.toTeamsUser.userStatus !== UserStatusV2.VALID_READY &&
                    entry.toTeams.toTeamsUser.userStatus !== UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED
                  ? entry.toTeams.toTeamsUser.statusMessage
                      ?.replace(
                        'Microsoft Teams Phone Standard',
                        '<i><strong>Microsoft Teams Phone Standard</strong></i>',
                      )
                      .replace('Enterprise Voice', '<i><strong>Enterprise Voice</strong></i>')
                  : null,
            isDisabled: entry.migrationStatus === MigrationStatusV2.IGNORED,
            validation: [
              {
                validator: () => {
                  return [
                    UserStatusV2.NO_STATUS,
                    UserStatusV2.VALID_READY,
                    UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED,
                    UserStatusV2.STAGED_IN_BULK_DRAFT,
                  ].includes(entry.toTeams.toTeamsUser.userStatus)
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                },
                message: () => '',
              },
            ],
          },
          teamsNumber: teamsNumberObject,
        },
      };
    });
    const UILineTypeEntries = Object.entries(UiLineType);

    this.data = rows.sort((a, b) => {
      const aLineUsageType = UILineTypeEntries.find((entry) => entry[1] === a.lineUsageType)[0];
      const bLineUsageType = UILineTypeEntries.find((entry) => entry[1] === b.lineUsageType)[0];

      const aOrder = MigrationsService.LINE_TYPE_ROW_MAP.get(aLineUsageType as LineUsageType).initialSortOrder;
      const bOrder = MigrationsService.LINE_TYPE_ROW_MAP.get(bLineUsageType as LineUsageType).initialSortOrder;
      return aOrder - bOrder;
    });
    this._initFilterOptionLists();
    this._updateFilterCounts();
  }

  private _buildLineDisplayMarkup(key: LineUsageTypeV2): string {
    const config = MigrationsService.LINE_TYPE_ROW_MAP.get(key);
    if (!config) {
      return '';
    }

    const lineType = MigrationsService.LINE_TYPE_ROW_MAP.get(key).lineType;

    return `
    <span class="alert alert-inline alert-info">
      <i class="${config.icon} ${config.iconClass || ''}"></i>
      <span>${lineType}</span>
    </span>
  `;
  }

  getDeviceUsageTypeLabel(row: RingCentralNumberReportRow): string {
    return row.deviceUsageType;
  }

  getDeviceUsageTypeIcon(row: RingCentralNumberReportRow): ZiroIcons {
    return Array.from(MigrationsService.DEVICE_TYPE_ROW_MAP.values()).find(
      (value) => value.label === row.deviceUsageType,
    ).icon;
  }

  isIconStacked(icon: string): boolean {
    return Object.keys(ZiroStackedIcons).includes(icon);
  }

  private _buildAssociatedDeviceBadges(
    devices: AssociatedDevice[],
    tooltipKey: string,
  ): DatatableBadge<RingCentralNumberReportRow>[] {
    if (!devices.length) {
      return [];
    }

    const totalChars = devices.map((device) => device.name).reduce((sum, name) => sum + name.length, 0);

    if (totalChars <= this.MAX_ASSOCIATED_DEVICE_BADGE_LENGTH) {
      return devices.map((device) => ({ label: device.name }));
    }

    const moreTkey: string = this._translateService.instant(
      'tkey;migrations.number_report.table.associated_devices.more_devices.label',
    );
    const badges: DatatableBadge<RingCentralNumberReportRow>[] = [];
    let remainingNames = devices.length;
    let totalCharsInBadgesSoFar = 0;

    for (let i = 0; i < devices.length; i++) {
      const nameLength = devices[i].name.length;

      if (
        moreTkey.length + nameLength + remainingNames.toString().length + totalCharsInBadgesSoFar <=
        this.MAX_ASSOCIATED_DEVICE_BADGE_LENGTH
      ) {
        badges.push({ label: devices[i].name });
        totalCharsInBadgesSoFar += nameLength;
        remainingNames--;
      } else {
        break;
      }
    }

    const moreLabel = remainingNames + moreTkey;
    const namesTooLong = devices.length > 1 && !badges.length;

    if (badges.length) {
      badges.push({
        label: moreLabel,
        hardcodedTooltip: this._buildAssociatedDeviceNamesTooltip(
          devices.slice(devices.length - remainingNames),
          tooltipKey,
        ),
      });
    } else if (namesTooLong) {
      badges.push({
        label: this._translateService.instant(
          'tkey;migrations.number_report.table.associated_devices.all_devices.label',
          {
            count: devices.length,
          },
        ),
        hardcodedTooltip: this._buildAssociatedDeviceNamesTooltip(devices, tooltipKey),
      });
    } else {
      badges.push({
        label: devices[0].name,
        showTooltipOnTruncate: true,
      });
    }

    return badges;
  }

  private _buildAssociatedDeviceNamesTooltip(devices: AssociatedDevice[], tkey: string): string {
    const translatedTkey = this._translateService.instant(tkey);
    const formattedNames = devices.map((device) => `<li>${device.name}</li>`).join('');

    return `
    <div>
      <div>${translatedTkey}</div>
      <ul class="text-start mb-0">
      ${formattedNames}
      </ul>
    </div>`;
  }

  private _getUpnPhotoBorderColor(userStatus: UserStatusV2): string {
    switch (userStatus) {
      case UserStatusV2.VALID_READY:
      case UserStatusV2.NO_STATUS:
        return '';
      case UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED:
        return 'teams-user--migrated';
      case UserStatusV2.STAGED_IN_BULK_DRAFT:
        return 'teams-user--staged';
      default:
        return 'teams-user--error';
    }
  }

  private _onMs365UserInputSelect(entry: NumberReportEntry<RingCentralFields>, newUpn: string): void {
    const row = this.data.find((row) => row.phoneNumber === entry.number);
    row.isMigrationStatusPending = true;
    this._ringCentralNumberReportContext.updateTeamsUpn(entry.rowId, newUpn).subscribe();
  }

  private _getMoveToTeamsNumberObject(
    teamsObject: ToTeamsNumberV2,
    number: string,
    rowId: string,
    migrationStatus: MigrationStatusV2,
  ): DatatableTextField {
    const maxLength = 16;
    const cellWidth = '21ch';
    const onBlur = (inputValue: string, fieldState: DatatableTextField): void => {
      const oldIconHtmlPrepend = fieldState.iconHtmlPrepend;
      fieldState.iconHtmlPrepend = `<i class="text-warning fa-xl fa ${ZiroIcons.LOADING}"></i>`;
      let putValue = inputValue.trim();
      if (putValue === '+') {
        putValue = number;
      }
      if (inputValue.trim() !== teamsObject.number) {
        fieldState.value = '';
        const row = this.data.find((row) => row.id === rowId);
        row.isMigrationStatusPending = true;
        this._ringCentralNumberReportContext
          .updateTeamsNumber(rowId, putValue)
          .subscribe(() => (fieldState.iconHtmlPrepend = oldIconHtmlPrepend));
      } else {
        fieldState.iconHtmlPrepend = oldIconHtmlPrepend;
      }
    };
    const validation = [
      {
        validator: (): ZiroFormsValidationState =>
          teamsObject?.numberStatus !== NumberStatusV2.VALID_FORMAT
            ? ZiroFormsValidationState.INVALID
            : ZiroFormsValidationState.VALID,
        message: () =>
          teamsObject?.numberStatus === NumberStatusV2.INVALID_FORMAT
            ? this._translateService.instant('tkey;migrations.number_report.teams_number.validator.invalid_format')
            : teamsObject?.statusMessage,
      },
    ];
    const onBeforeInput = (keyboardEvent: InputEvent, inputValue: string): void => {
      const key = keyboardEvent.data;
      if (key >= '0' && key <= '9') {
        return;
      }

      // Allow '+' characters only at the start of the number
      const targetElement = keyboardEvent.target as HTMLInputElement;
      const firstPlusCharacter = key === '+' && !inputValue.includes('+') && targetElement.selectionStart === 0;
      const replacingEntireValueWithPlus =
        key === '+' && targetElement.selectionStart === 0 && targetElement.selectionEnd === inputValue.length;
      const replacingExistingPlus =
        key === '+' &&
        inputValue.startsWith('+') &&
        targetElement.selectionStart === 0 &&
        targetElement.selectionEnd === 1;

      if (firstPlusCharacter || replacingEntireValueWithPlus || replacingExistingPlus) {
        return;
      }

      keyboardEvent.preventDefault();
    };
    const onPaste = (clipboardEvent: ClipboardEvent): void => {
      clipboardEvent.preventDefault();

      const pastedText = clipboardEvent.clipboardData.getData('text').trim();
      const targetElement = clipboardEvent.target as HTMLInputElement;
      const beforePastedText = targetElement.value.slice(0, targetElement.selectionStart);
      const afterPastedText = targetElement.value.slice(targetElement.selectionEnd, targetElement.value.length);

      let sanitized = beforePastedText + pastedText + afterPastedText;
      sanitized = sanitized[0] + sanitized.slice(1, sanitized.length).replaceAll('+', '');
      sanitized = sanitized.replace(/[^\d+]/g, '').slice(0, maxLength);

      targetElement.value = sanitized;
      targetElement.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const isIgnoredRow = migrationStatus === MigrationStatusV2.IGNORED;

    const baseField: DatatableTextField = {
      type: DatatableFieldTypes.TEXT,
      value: teamsObject?.number ?? '',
      maxLength,
      cellWidth,
      onBlur,
      onBeforeInput,
      onPaste,
      isDisabled: isIgnoredRow,
      validation,
    };

    if (!teamsObject?.number || teamsObject.teamsNumberSource === TeamsNumberSource.NUMBER_FROM_SOURCE) {
      return {
        ...baseField,
        tooltip: isIgnoredRow
          ? this._translateService.instant('tkey;migrations.number_report.ignored.row.disabled.tooltip')
          : '',
      };
    }

    const isTranslatedNumber = teamsObject.teamsNumberSource === TeamsNumberSource.TRANSLATED_NUMBER;
    return {
      ...baseField,
      iconHtmlPrepend: isTranslatedNumber ? this.translationsIcon : this.manualEntryIcon,
      // add another condition to this tooltip when we add number translations to this report.
      tooltip: isIgnoredRow
        ? this._translateService.instant('tkey;migrations.number_report.ignored.row.disabled.tooltip')
        : '',
    };
  }

  private _getIconHtml(icon: ZiroIcons): string {
    return `
    <span class="fa-stack">
      <i class="fas ${ZiroIcons.CIRCLE} fa-stack-2x"></i>
      <i class="fas ${icon} fa-stack-1x fa-inverse"></i>
    </span>`;
  }

  onTableResultsChanged(): void {
    this._updateFilterCounts();
  }

  private _initFilterOptionLists(): void {
    this.migrationStatusOptionsWithCounts = Object.values(UiMigrationStatus).map((v) => ({
      value: v,
      label: v,
      count: 0,
    }));

    this.lineTypeOptionsWithCounts = this._lineTypeOptionsInOrder.map((option) => ({
      value: option,
      label: option,
      count: 0,
    }));

    this.deviceTypeOptionsWithCounts = [
      { value: 'No Device', label: 'No Device', count: 0 },
      ...Array.from(MigrationsService.DEVICE_TYPE_ROW_MAP.values())
        .filter((info) => info.label !== 'No Device')
        .map((info) => ({ value: info.label, label: info.label, count: 0 })),
    ];
  }

  private _updateFilterCounts(): void {
    if (!this.datatableComponent?.filteredData) {
      return;
    }

    const migrationStatusCounts = new Map<string, number>();
    const lineTypeCounts = new Map<string, number>();
    const deviceTypeCounts = new Map<string, number>();

    this.datatableComponent.filteredData.forEach((row) => {
      const ms = row.migrationStatus;
      migrationStatusCounts.set(ms, (migrationStatusCounts.get(ms) || 0) + 1);

      const lt = row.lineUsageType;
      lineTypeCounts.set(lt, (lineTypeCounts.get(lt) || 0) + 1);

      const dt = row.deviceUsageType;
      deviceTypeCounts.set(dt, (deviceTypeCounts.get(dt) || 0) + 1);
    });

    this.migrationStatusOptionsWithCounts.forEach((opt) => {
      opt.count = migrationStatusCounts.get(opt.value) ?? 0;
    });
    this.lineTypeOptionsWithCounts.forEach((opt) => {
      opt.count = lineTypeCounts.get(opt.value) ?? 0;
    });
    this.deviceTypeOptionsWithCounts.forEach((opt) => {
      opt.count = deviceTypeCounts.get(opt.value) ?? 0;
    });
  }
}
